<script setup>
import { useAuth } from "@/composables/useAuth";
import { useRouter } from "vue-router";
import Swal from "sweetalert2";
import MiniStatisticsCard from "@/quote/Cards/MiniStatisticsCard.vue";
import GradientLineChart from "@/quote/Charts/GradientLineChart.vue";
import { ref, computed, onMounted, onUnmounted } from "vue";
import api from "../api/axios";
import { destinosDisponibles, paisesDestinos } from "@/utils/destinos";

const { user, loadUser, hasRole } = useAuth();
const router = useRouter();

const cargando = ref(true);
const data = ref(null);
const topDestinations = ref([]);
let intervalo = null;

async function cargarDashboard() {
  const [home, destinos] = await Promise.all([
    api.get("/dashboard/home"),
    api.get("/quotes/top-destinations")
  ]);
  data.value = home.data;
  topDestinations.value = destinos.data;
}

onMounted(async () => {
  if (!user.value) await loadUser();

  cargando.value = true;
  await cargarDashboard();
  cargando.value = false;

  intervalo = setInterval(cargarDashboard, 60000);
});

onUnmounted(() => {
  if (intervalo) clearInterval(intervalo);
});

// ===== Encabezado =====
const saludo = computed(() => {
  const hora = new Date().getHours();
  if (hora < 12) return "Buenos días";
  if (hora < 19) return "Buenas tardes";
  return "Buenas noches";
});

const fechaHoy = computed(() => {
  const hoy = new Date().toLocaleDateString("es-CO", { weekday: "long", day: "numeric", month: "long" });
  return hoy.charAt(0).toUpperCase() + hoy.slice(1);
});

const primerNombre = computed(() => (user.value?.name || "").split(" ")[0] || "");

// ===== Acciones rápidas (sin salir del Dashboard) =====
function irANuevaCotizacion() {
  router.push("/quote/create");
}

async function nuevoCliente() {
  const { value } = await Swal.fire({
    title: "Nuevo cliente",
    html:
      '<input id="swal-nombre" class="swal2-input" placeholder="Nombre">' +
      '<input id="swal-telefono" class="swal2-input" placeholder="Teléfono">',
    focusConfirm: false,
    showCancelButton: true,
    confirmButtonText: "Crear",
    cancelButtonText: "Cancelar",
    preConfirm: () => {
      const name = document.getElementById("swal-nombre").value.trim();
      const phone = document.getElementById("swal-telefono").value.trim();
      if (!name) {
        Swal.showValidationMessage("El nombre es obligatorio");
        return false;
      }
      return { name, phone };
    }
  });

  if (!value) return;

  try {
    await api.post("/crm/contacts", value);
    await cargarDashboard();
    Swal.fire({ icon: "success", title: "Cliente creado", timer: 1500, showConfirmButton: false });
  } catch (err) {
    Swal.fire({ icon: "error", title: "No se pudo crear el cliente", text: err.response?.data?.error || "Error de conexión." });
  }
}

async function nuevaTarea() {
  const { value } = await Swal.fire({
    title: "Nueva tarea",
    html:
      '<input id="swal-titulo" class="swal2-input" placeholder="Título">' +
      '<input id="swal-fecha" type="datetime-local" class="swal2-input">',
    focusConfirm: false,
    showCancelButton: true,
    confirmButtonText: "Crear",
    cancelButtonText: "Cancelar",
    preConfirm: () => {
      const title = document.getElementById("swal-titulo").value.trim();
      const dueAt = document.getElementById("swal-fecha").value;
      if (!title) {
        Swal.showValidationMessage("El título es obligatorio");
        return false;
      }
      return { title, dueAt: dueAt || null };
    }
  });

  if (!value) return;

  try {
    await api.post("/crm/tasks", value);
    await cargarDashboard();
    Swal.fire({ icon: "success", title: "Tarea creada", timer: 1500, showConfirmButton: false });
  } catch (err) {
    Swal.fire({ icon: "error", title: "No se pudo crear la tarea", text: err.response?.data?.error || "Error de conexión." });
  }
}

async function nuevaReunion() {
  const { value } = await Swal.fire({
    title: "Nueva reunión",
    html:
      '<input id="swal-notas" class="swal2-input" placeholder="Con quién / motivo">' +
      '<input id="swal-fecha" type="datetime-local" class="swal2-input">',
    focusConfirm: false,
    showCancelButton: true,
    confirmButtonText: "Crear",
    cancelButtonText: "Cancelar",
    preConfirm: () => {
      const notes = document.getElementById("swal-notas").value.trim();
      const startsAt = document.getElementById("swal-fecha").value;
      if (!startsAt) {
        Swal.showValidationMessage("La fecha y hora son obligatorias");
        return false;
      }
      return { notes, startsAt, type: "reunion" };
    }
  });

  if (!value) return;

  try {
    await api.post("/crm/activities", value);
    await cargarDashboard();
    Swal.fire({ icon: "success", title: "Reunión agendada", timer: 1500, showConfirmButton: false });
  } catch (err) {
    Swal.fire({ icon: "error", title: "No se pudo agendar la reunión", text: err.response?.data?.error || "Error de conexión." });
  }
}

// ===== Prioridades (clicables) =====
const prioridadesConfig = computed(() => {
  if (!data.value) return [];
  const p = data.value.prioridades;

  return [
    { key: "clientesSinResponder", label: "clientes sin responder", valor: p.clientesSinResponder, icon: "bi-chat-dots", ruta: "/crm/inbox" },
    { key: "cotizacionesPendientes", label: "cotizaciones pendientes", valor: p.cotizacionesPendientes, icon: "bi-file-earmark-text", ruta: "/quote/list" },
    { key: "tareasVencidas", label: "tareas vencidas", valor: p.tareasVencidas, icon: "bi-exclamation-triangle", ruta: "/crm/tasks" },
    { key: "seguimientosHoy", label: "seguimientos para hoy", valor: p.seguimientosHoy, icon: "bi-calendar-check", ruta: "/crm/agenda" },
    { key: "conexionesConProblema", label: "conexión requiere atención", valor: p.conexionesConProblema, icon: "bi-plug", ruta: "/crm/settings" }
  ].filter(item => item.valor > 0);
});

// ===== Formato de fecha/hora relativa =====
function formatHora(fecha) {
  if (!fecha) return "";
  return new Date(fecha).toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" });
}

function tiempoRelativo(fecha) {
  if (!fecha) return "Sin actividad registrada";
  const segundos = Math.floor((Date.now() - new Date(fecha).getTime()) / 1000);
  if (segundos < 60) return "Hace un momento";
  const minutos = Math.floor(segundos / 60);
  if (minutos < 60) return `Hace ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `Hace ${horas} h`;
  const dias = Math.floor(horas / 24);
  return `Hace ${dias} día${dias === 1 ? "" : "s"}`;
}

function estadoQuoteBadge(status) {
  return {
    pendiente: "bg-gradient-secondary",
    enviada: "bg-info text-dark",
    aceptada: "bg-success",
    rechazada: "bg-danger"
  }[status] || "bg-gradient-secondary";
}

function estadoQuoteLabel(status) {
  return { pendiente: "Pendiente", enviada: "Enviada", aceptada: "Aceptada", rechazada: "Rechazada" }[status] || status;
}

// ===== Destinos (ranking existente, reusado en Estadísticas del mes) =====
const nombreDestino = (codigo) => destinosDisponibles.find(d => d.value === codigo)?.label ?? codigo;
const banderaDestino = (codigo) => {
  const iso = paisesDestinos[codigo];
  return iso ? `https://flagcdn.com/w40/${iso}.png` : null;
};
const valorPromedio = (valor) => Math.round(Number(valor)).toLocaleString("es-CO");
const maxCantidadDestino = computed(() => Math.max(1, ...topDestinations.value.map(d => Number(d.cantidad) || 0)));
const maxCantidadEmbudo = computed(() => Math.max(1, ...(data.value?.embudo || []).map(e => Number(e.cantidad) || 0)));
const maxCantidadAsesor = computed(() => Math.max(1, ...(data.value?.estadisticasDelMes.ventasPorAsesor || []).map(v => Number(v.cantidad) || 0)));

const chartVentasCotizaciones = computed(() => {
  const meses = data.value?.estadisticasDelMes.cotizacionesPorMes || [];
  const ventas = data.value?.estadisticasDelMes.ventasPorMes || [];

  return {
    labels: ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"],
    datasets: [
      { label: "Cotizaciones", data: meses.map(m => m.total) },
      { label: "Ventas", data: ventas.map(v => v.total) }
    ]
  };
});

function estadoPillClass(status) {
  return { connected: "bg-success", online: "bg-success", operativa: "bg-success", disconnected: "bg-gradient-secondary", error: "bg-danger", con_errores: "bg-danger", con_problemas: "bg-danger", sin_configurar: "bg-gradient-secondary" }[status] || "bg-gradient-secondary";
}

function nombreCanal(channel) {
  return { whatsapp: "WhatsApp", instagram: "Instagram", messenger: "Messenger" }[channel] || channel;
}

const canalesCompletos = computed(() => {
  const existentes = data.value?.estadoSistema.canales || [];
  const nombres = ["whatsapp", "instagram", "messenger"];
  return nombres.map(ch => existentes.find(c => c.channel === ch) || { channel: ch, status: "disconnected" });
});
</script>

<template>
  <div class="py-4 container-fluid dashboard-page">

    <!-- ENCABEZADO -->
    <div class="dashboard-hero d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
      <div>
        <p class="hero-eyebrow mb-1">{{ fechaHoy }}</p>
        <h4 class="mb-0 dashboard-greeting">{{ saludo }}<span v-if="primerNombre">, {{ primerNombre }}</span> 👋</h4>
        <p class="text-muted mb-0 mt-1 dashboard-company">{{ user?.companyName }}</p>
      </div>
      <div class="d-flex flex-wrap gap-2">
        <button class="btn btn-success d-flex align-items-center gap-2" @click="irANuevaCotizacion">
          <i class="bi bi-file-earmark-plus"></i> Nueva Cotización
        </button>
        <button class="btn btn-outline-primary quick-action-btn d-flex align-items-center gap-2" @click="nuevoCliente">
          <i class="bi bi-person-plus"></i> Cliente
        </button>
        <button class="btn btn-outline-primary quick-action-btn d-flex align-items-center gap-2" @click="nuevaTarea">
          <i class="bi bi-check2-square"></i> Tarea
        </button>
        <button class="btn btn-outline-primary quick-action-btn d-flex align-items-center gap-2" @click="nuevaReunion">
          <i class="bi bi-calendar-plus"></i> Reunión
        </button>
      </div>
    </div>

    <div v-if="cargando" class="text-center py-5">
      <div class="spinner-border text-success" role="status"></div>
    </div>

    <template v-else-if="data">

      <!-- NEXA IA -->
      <div class="card nexa-ia-card mb-4">
        <div class="card-body d-flex align-items-start gap-3">
          <div class="nexa-ia-icon"><i class="bi bi-stars"></i></div>
          <div>
            <h6 class="mb-1 nexa-ia-title">Nexa IA</h6>
            <p class="mb-0 nexa-ia-message" style="color: black !important;">{{ data.nexaIA.mensaje }}</p>
          </div>
        </div>
      </div>

      <!-- PRIORIDADES + RESUMEN DEL DÍA -->
      <div class="row g-3 mb-3">
        <div class="col-lg-6">
          <div class="card h-100">
            <div class="p-3 pb-0 card-header"><h6 class="mb-0"><i class="bi bi-fire text-danger me-1"></i> Prioridades</h6></div>
            <div class="card-body pt-2">
              <div v-if="prioridadesConfig.length === 0" class="text-muted small text-center py-4">
                No tenés pendientes urgentes hoy. 🎉
              </div>
              <div
                v-for="p in prioridadesConfig" :key="p.key"
                class="priority-row"
                @click="router.push(p.ruta)"
              >
                <i class="bi" :class="p.icon"></i>
                <span class="flex-grow-1">{{ p.valor }} {{ p.label }}</span>
                <i class="bi bi-chevron-right text-muted"></i>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-6">
          <div class="card h-100">
            <div class="p-3 pb-0 card-header"><h6 class="mb-0"><i class="bi bi-bar-chart-line text-primary me-1"></i> Resumen del día</h6></div>
            <div class="card-body pt-2">
              <div class="row g-2">
                <div class="col-6"><mini-statistics-card title="Cotizaciones" :value="data.resumenDelDia.cotizaciones" :icon="{ component: 'bi bi-file-earmark-text', background: 'bg-gradient-primary', shape: 'rounded-circle' }" /></div>
                <div class="col-6"><mini-statistics-card title="Ventas" :value="data.resumenDelDia.ventas" :icon="{ component: 'bi bi-trophy', background: 'bg-gradient-success', shape: 'rounded-circle' }" /></div>
                <div class="col-6"><mini-statistics-card title="Conversaciones" :value="data.resumenDelDia.conversaciones" :icon="{ component: 'bi bi-chat-dots', background: 'bg-gradient-info', shape: 'rounded-circle' }" /></div>
                <div class="col-6"><mini-statistics-card title="Reuniones" :value="data.resumenDelDia.reuniones" :icon="{ component: 'bi bi-calendar-week', background: 'bg-gradient-warning', shape: 'rounded-circle' }" /></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- AGENDA + CHATS RECIENTES -->
      <div class="row g-3 mb-3">
        <div class="col-lg-6">
          <div class="card h-100">
            <div class="p-3 pb-0 card-header"><h6 class="mb-0"><i class="bi bi-calendar-week text-primary me-1"></i> Agenda</h6></div>
            <div class="card-body pt-2">
              <div v-if="data.agendaHoy.length === 0" class="text-muted small text-center py-4">Sin nada agendado para hoy.</div>
              <div v-for="(item, i) in data.agendaHoy" :key="i" class="agenda-row">
                <span class="agenda-hora">{{ formatHora(item.hora) }}</span>
                <span class="flex-grow-1">{{ item.titulo }}</span>
              </div>
              <button class="btn btn-link btn-sm ps-0 mt-2" @click="router.push('/crm/agenda')">Ver agenda completa →</button>
            </div>
          </div>
        </div>

        <div class="col-lg-6">
          <div class="card h-100">
            <div class="p-3 pb-0 card-header"><h6 class="mb-0"><i class="bi bi-chat-dots text-primary me-1"></i> Chats recientes</h6></div>
            <div class="card-body pt-2">
              <div v-if="data.chatsRecientes.length === 0" class="text-muted small text-center py-4">Sin conversaciones todavía.</div>
              <div v-for="chat in data.chatsRecientes" :key="chat.id" class="ranking-row">
                <div class="ranking-info">
                  <div class="d-flex justify-content-between">
                    <h6 class="mb-0 text-sm">{{ chat.contact_name }}</h6>
                    <span class="text-xs text-muted">{{ tiempoRelativo(chat.last_message_at) }}</span>
                  </div>
                  <p class="text-xs text-muted mb-0 text-truncate">{{ chat.last_message || "Sin mensajes" }}</p>
                </div>
              </div>
              <button class="btn btn-link btn-sm ps-0 mt-2" @click="router.push('/crm/inbox')">Abrir Inbox →</button>
            </div>
          </div>
        </div>
      </div>

      <!-- COTIZACIONES + CLIENTES SIN SEGUIMIENTO -->
      <div class="row g-3 mb-3">
        <div class="col-lg-6">
          <div class="card h-100">
            <div class="p-3 pb-0 card-header"><h6 class="mb-0"><i class="bi bi-file-earmark-text text-primary me-1"></i> Cotizaciones</h6></div>
            <div class="card-body pt-2">
              <div v-if="data.cotizacionesRecientes.length === 0" class="text-muted small text-center py-4">Sin cotizaciones registradas.</div>
              <div v-for="q in data.cotizacionesRecientes" :key="q.id" class="ranking-row">
                <div class="ranking-info">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0 text-sm">{{ nombreDestino(q.destination) }}</h6>
                    <span class="badge" :class="estadoQuoteBadge(q.status)">{{ estadoQuoteLabel(q.status) }}</span>
                  </div>
                  <span class="text-xs text-muted">${{ valorPromedio(q.total_price) }}</span>
                </div>
              </div>
              <button class="btn btn-link btn-sm ps-0 mt-2" @click="router.push('/quote/list')">Ver todas →</button>
            </div>
          </div>
        </div>

        <div class="col-lg-6">
          <div class="card h-100">
            <div class="p-3 pb-0 card-header"><h6 class="mb-0"><i class="bi bi-people text-primary me-1"></i> Clientes sin seguimiento</h6></div>
            <div class="card-body pt-2">
              <div v-if="data.clientesSinSeguimiento.length === 0" class="text-muted small text-center py-4">Todos tus clientes tienen seguimiento reciente. 🎉</div>
              <div v-for="c in data.clientesSinSeguimiento" :key="c.id" class="ranking-row">
                <div class="ranking-info d-flex justify-content-between align-items-center">
                  <div>
                    <h6 class="mb-0 text-sm">{{ c.name }}</h6>
                    <span class="text-xs text-muted">Último contacto: {{ tiempoRelativo(c.last_activity_at) }}</span>
                  </div>
                  <button class="btn btn-sm btn-outline-success" @click="router.push(`/crm/contacts/${c.id}`)">Seguimiento</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- EMBUDO + ACTIVIDAD RECIENTE -->
      <div class="row g-3 mb-3">
        <div class="col-lg-6">
          <div class="card h-100">
            <div class="p-3 pb-0 card-header"><h6 class="mb-0"><i class="bi bi-funnel text-primary me-1"></i> Embudo</h6></div>
            <div class="card-body pt-2">
              <div v-if="data.embudo.length === 0" class="text-muted small text-center py-4">Sin etapas configuradas.</div>
              <div v-for="stage in data.embudo" :key="stage.id" class="ranking-row">
                <div class="ranking-info">
                  <div class="d-flex justify-content-between">
                    <h6 class="mb-0 text-sm">{{ stage.name }}</h6>
                    <span class="text-xs text-muted">{{ stage.cantidad }}</span>
                  </div>
                  <div class="ranking-bar">
                    <div class="ranking-bar-fill bg-gradient-info" :style="{ width: (stage.cantidad / maxCantidadEmbudo * 100) + '%' }"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-6">
          <div class="card h-100">
            <div class="p-3 pb-0 card-header"><h6 class="mb-0"><i class="bi bi-activity text-primary me-1"></i> Actividad reciente</h6></div>
            <div class="card-body pt-2">
              <div v-if="data.actividadReciente.length === 0" class="text-muted small text-center py-4">Sin actividad registrada todavía.</div>
              <div v-for="act in data.actividadReciente" :key="act.id" class="activity-row">
                <span>{{ act.author_name || "Alguien" }} — {{ act.description }}</span>
                <span class="text-xs text-muted">{{ tiempoRelativo(act.createdAt) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ESTADÍSTICAS DEL MES -->
      <p class="section-eyebrow mt-2">Estadísticas del mes</p>
      <div class="row g-3 mb-3">
        <div class="col-12 mb-3">
          <gradient-line-chart id="chart-ventas-cotizaciones" title="Cotizaciones vs Ventas" :chart="chartVentasCotizaciones" />
        </div>

        <div class="col-lg-6">
          <div class="card h-100">
            <div class="p-3 pb-0 card-header"><h6 class="mb-0"><i class="bi bi-globe-americas text-primary me-1"></i> Cotizaciones por destino</h6></div>
            <div class="card-body pt-2">
              <div v-if="topDestinations.length === 0" class="text-muted small text-center py-4">Todavía no hay cotizaciones registradas.</div>
              <div v-for="(dest, index) in topDestinations" :key="index" class="ranking-row">
                <span class="ranking-position">{{ index + 1 }}</span>
                <img v-if="banderaDestino(dest.destination)" :src="banderaDestino(dest.destination)" :alt="`Bandera de ${nombreDestino(dest.destination)}`" class="avatar avatar-sm rounded-circle" style="object-fit: cover;" />
                <span v-else class="avatar avatar-sm rounded-circle d-flex align-items-center justify-content-center bg-gradient-info"><i class="bi bi-globe-americas text-white"></i></span>
                <div class="ranking-info">
                  <div class="d-flex justify-content-between">
                    <h6 class="mb-0 text-sm">{{ nombreDestino(dest.destination) }}</h6>
                    <span class="text-xs text-muted">{{ dest.cantidad }} · ${{ valorPromedio(dest.valor_promedio) }} prom.</span>
                  </div>
                  <div class="ranking-bar"><div class="ranking-bar-fill bg-gradient-primary" :style="{ width: (dest.cantidad / maxCantidadDestino * 100) + '%' }"></div></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="hasRole('admin')" class="col-lg-6">
          <div class="card h-100">
            <div class="p-3 pb-0 card-header"><h6 class="mb-0"><i class="bi bi-award text-success me-1"></i> Ventas por asesor</h6></div>
            <div class="card-body pt-2">
              <div v-if="data.estadisticasDelMes.ventasPorAsesor.length === 0" class="text-muted small text-center py-4">Sin ventas este mes todavía.</div>
              <div v-for="(v, index) in data.estadisticasDelMes.ventasPorAsesor" :key="index" class="ranking-row">
                <span class="ranking-position">{{ index + 1 }}</span>
                <span class="avatar avatar-sm rounded-circle d-flex align-items-center justify-content-center bg-gradient-success text-white text-xs font-weight-bold">{{ (v.name || "?").charAt(0).toUpperCase() }}</span>
                <div class="ranking-info">
                  <div class="d-flex justify-content-between">
                    <h6 class="mb-0 text-sm">{{ v.name }}</h6>
                    <span class="text-xs text-muted">{{ v.cantidad }} ventas</span>
                  </div>
                  <div class="ranking-bar"><div class="ranking-bar-fill bg-gradient-success" :style="{ width: (v.cantidad / maxCantidadAsesor * 100) + '%' }"></div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ESTADO DEL SISTEMA -->
      <div v-if="hasRole('admin')" class="card mb-3">
        <div class="p-3 pb-0 card-header"><h6 class="mb-0"><i class="bi bi-hdd-network text-primary me-1"></i> Estado del sistema</h6></div>
        <div class="card-body pt-2">
          <div class="row g-2 text-center">
            <div v-for="canal in canalesCompletos" :key="canal.channel" class="col-6 col-lg-3">
              <div class="system-status-pill">
                <span class="badge" :class="estadoPillClass(canal.status)">&nbsp;</span>
                <div class="small mt-1">{{ nombreCanal(canal.channel) }}</div>
              </div>
            </div>
            <div class="col-6 col-lg-3">
              <div class="system-status-pill">
                <span class="badge" :class="estadoPillClass(data.estadoSistema.apiVuelos)">&nbsp;</span>
                <div class="small mt-1">API vuelos</div>
              </div>
            </div>
            <div class="col-6 col-lg-3">
              <div class="system-status-pill">
                <span class="badge" :class="estadoPillClass(data.estadoSistema.servidor)">&nbsp;</span>
                <div class="small mt-1">Servidor</div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </template>
  </div>
</template>

<style scoped>
.hero-eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--bs-primary, #2f8cf0);
}

.section-eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 0.7rem;
  font-weight: 700;
  color: #8a8a9a;
  margin-bottom: 0.5rem;
}

.dashboard-hero {
  padding: 1.25rem 1.5rem;
  border-radius: 1rem;
  background: linear-gradient(120deg, rgba(109, 66, 245, 0.08), rgba(47, 140, 240, 0.06));
}

.nexa-ia-card {
  background: linear-gradient(120deg, rgba(109, 66, 245, 0.12), rgba(47, 140, 240, 0.08));
  border: none;
}

.nexa-ia-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: linear-gradient(310deg, #7928ca, #ff0080);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.priority-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0;
  border-bottom: 1px solid rgba(11, 11, 24, 0.06);
  cursor: pointer;
}

.priority-row:last-child {
  border-bottom: none;
}

.priority-row:hover {
  color: var(--bs-primary, #2f8cf0);
}

.agenda-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(11, 11, 24, 0.06);
}

.agenda-row:last-child {
  border-bottom: none;
}

.agenda-hora {
  flex: 0 0 3.5rem;
  font-weight: 700;
  font-size: 0.8rem;
  color: #8a8a9a;
}

.activity-row {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(11, 11, 24, 0.06);
  font-size: 0.85rem;
}

.activity-row:last-child {
  border-bottom: none;
}

.system-status-pill .badge {
  width: 0.85rem;
  height: 0.85rem;
  padding: 0;
  border-radius: 50%;
}

.ranking-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0;
  border-bottom: 1px solid rgba(11, 11, 24, 0.06);
}

.ranking-row:last-child {
  border-bottom: none;
}

.ranking-position {
  flex: 0 0 1.5rem;
  text-align: center;
  font-weight: 700;
  font-size: 0.75rem;
  color: #8a8a9a;
}

.ranking-info {
  flex: 1 1 auto;
  min-width: 0;
}

.ranking-bar {
  height: 4px;
  border-radius: 4px;
  background: rgba(11, 11, 24, 0.06);
  margin-top: 0.35rem;
  overflow: hidden;
}

.ranking-bar-fill {
  height: 100%;
  border-radius: 4px;
}

@media (max-width: 575.98px) {
  .dashboard-hero {
    padding: 1rem;
  }

  .dashboard-hero .btn {
    width: 100%;
    justify-content: center;
  }
}

/* Blanco siempre (claro y oscuro): estos elementos van sobre fondos
   degradados de color (dashboard-hero, nexa-ia-card) donde el texto oscuro
   por defecto se pierde. */
.hero-eyebrow,
.dashboard-greeting,
.dashboard-company,
.nexa-ia-title,
.nexa-ia-message {
  color: #fff !important;
}

.quick-action-btn {
  color: #fff !important;
  border-color: #fff !important;
}
</style>
